import React, { useState } from "react";
import { CircularProgress, Grid, IconButton } from "@mui/material";
import { useIntl } from "react-intl";
import { Modal } from "@packsize/components";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";

import { type MachineGroup } from "@/schemas";
import { DiffView } from "@/components/diff-view";
import { useMachineGroupConfigurationHistory } from "@/hooks/machine-groups";
import { sanitizeMachineGroupAuditData } from "@/utils/sanitize-audit-data";
import type { Machine } from "@/schemas/machine";

interface MachineGroupHistoryButtonProps {
	readonly machineGroup: MachineGroup;
	readonly machineList: Machine[];
}

export const MachineGroupHistoryButton: React.FC<MachineGroupHistoryButtonProps> = ({ machineGroup, machineList }) => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const historyData = useMachineGroupConfigurationHistory(machineGroup.id, isOpen);

	const loadingHistoryData = historyData.isLoading || historyData.isFetchedAfterMount === false;

	return (
		<>
			<Modal
				onClose={() => setIsOpen(false)}
				title={intl.formatMessage(
					{
						id: "Machine Group History: {alias}",
						defaultMessage: "Machine Group History: {alias}",
					},
					{ alias: machineGroup.alias },
				)}
				open={isOpen}
				size="xl"
				slotProps={{
					contentContainer: {
						sx: {
							p: 0,
							overflowY: "scroll",
						},
					},
				}}
			>
				{loadingHistoryData === true && (
					<Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
						<CircularProgress color="info" data-testid="machine-group-history-button__progress__loading" />
					</Grid>
				)}
				{loadingHistoryData === false && (
					<DiffView
						data={historyData.data}
						compareAll
						actionColumn={null}
						additionalDataTransform={(data) => sanitizeMachineGroupAuditData(data, machineList)}
					/>
				)}
			</Modal>
			<IconButton
				onClick={() => setIsOpen(true)}
				data-testid="machine-group-history-button__button__open-modal"
				data-pendoid="machine-groups__button__history"
			>
				<HistoryOutlined sx={{ fontSize: 24, color: "text.primary" }} />
			</IconButton>
		</>
	);
};
